import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useConnections, useEvents, useMigration, useSignoff, useSignoffWatch } from "../../api/hooks";
import type { MigrationState } from "../../api/types";
import { formatDate } from "../../api/types";
import { formatRelativePast } from "../../i18n/relativeTime";
import { describeApiError } from "../../api/errors";
import { Banner, Button, ConfirmDialog, Spinner } from "../../components";
import { t, tDynamic } from "../../i18n/t";
import { MigrationPage } from "../migrationPage";
import { pathForStage, stageForState } from "../stageRoute";
import { describeConnection } from "../connect/ConnectScreen";
import { AttachmentsPanel } from "./AttachmentsPanel";
import { DeltaPanel } from "./DeltaPanel";
import { EvidencePanel } from "./EvidencePanel";
import { FailurePanel } from "./FailurePanel";
import { HmrcVerificationPanel } from "./HmrcVerificationPanel";
import { Timeline } from "./Timeline";

/** §B6.7: the three states that render the failure surface. */
const FAILURE_STATES: ReadonlySet<string> = new Set(["failed", "rolling_back", "rolled_back"]);

export function StatusScreen(): JSX.Element {
  const { migrationId } = useParams();
  const id = migrationId ?? "";
  const migration = useMigration(id === "" ? null : id);
  const state = migration.data?.state as MigrationState | undefined;
  // Every hook is called before the first early return: §B1.2's polling intervals live inside them.
  const events = useEvents(id === "" ? null : id, state);
  const signoff = useSignoff(id);
  const signoffWatch = useSignoffWatch(id, state);
  const signoffBusy = signoff.isPending || signoffWatch.waiting;
  // The h1 is the client organisation (Status.html), from the source connection.
  const connections = useConnections({ includeSuperseded: true });
  const [locking, setLocking] = useState(false);
  const [signoffError, setSignoffError] = useState<string | null>(null);

  if (migration.isLoading || state === undefined) {
    return <Spinner />;
  }

  const stateLabel = tDynamic("state.", state);
  const sourceConnection = (connections.data ?? []).find(
    (row) => row.id === migration.data?.source_connection_id,
  );
  const title = sourceConnection === undefined ? t("status.title") : describeConnection(sourceConnection).org;

  // StatusFailed.html's rail marks the stage the migration stopped AT — the failure event's own
  // `retry_target`, a state name, mapped back through the same machine.
  const newest = events.events[0] as { payload?: unknown } | undefined;
  const payload =
    typeof newest?.payload === "object" && newest.payload !== null
      ? (newest.payload as Record<string, unknown>)
      : {};
  const retryTarget = typeof payload.retry_target === "string" ? payload.retry_target : null;
  const stoppedStage =
    FAILURE_STATES.has(state) && retryTarget !== null ? stageForState(retryTarget as MigrationState) : null;

  const hypercareUntil = migration.data?.hypercare_until ?? null;
  const daysLeft =
    hypercareUntil === null
      ? null
      : Math.max(0, Math.ceil((Date.parse(hypercareUntil) - Date.now()) / 86_400_000));

  return (
    <MigrationPage
      id={id}
      migration={migration.data}
      title={title}
      meta={t("migrationheader.meta_status", {
        n: migration.data?.load_attempt ?? 0,
        when: formatRelativePast(migration.data?.updated_at ?? ""),
      })}
      announce={t("status.live", { state: stateLabel })}
      stoppedStage={stoppedStage}
      activity={false}
      // xmg-lmux (3): the timeline's own feed drives the live refresh; no second (limit=5) events poll.
      eventsFeed={{ events: events.data === undefined ? undefined : events.events }}
    >
      {FAILURE_STATES.has(state) ? (
        <FailurePanel migrationId={id} />
      ) : (
        <>
          {/* Critique addendum C2: status-only here -- the button is gone by construction once the
              migration has left `awaiting_conversion_signoff` (`hmrc_rerun_refusal` then refuses on state). */}
          <HmrcVerificationPanel migrationId={id} />
          {state === "hypercare" ? (
            <section className="card hypercare" aria-labelledby="hypercare-title">
              <div className="hypercare__main">
                <span className="eyebrow">{stateLabel}</span>
                <h2 id="hypercare-title" className="hypercare__title">
                  {t("status.hypercare_runs_until", { date: formatDate(hypercareUntil) })}
                </h2>
                <span className="muted">
                  {t("delta.sweep")} {t("status.lock_note")}
                </span>
                {(() => {
                  const blocking = migration.data?.open_blocking_reviews ?? 0;
                  return blocking > 0 ? (
                    <Banner tone="block">
                      {t("status.hypercare_blocking", { count: blocking })}{" "}
                      <Link to={pathForStage(id, "review")}>{t("status.hypercare_blocking_link")}</Link>
                    </Banner>
                  ) : null;
                })()}
              </div>
              {/*
                §C2.3 step 8 and §A9.5: signing off hypercare moves `hypercare -> locked`, which is TERMINAL,
                so nothing is deferred afterwards and the UI makes no second call. Once locked the button is
                gone. design doc hypercare-signoff-gates §5/K4/Final decision 7: hypercare is a real sign-off
                gate (`open_blocking_reviews > 0` refuses it, both in the runner and the API precondition).
              */}
              {daysLeft === null ? null : (
                <div className="hypercare__days">
                  <span className="hypercare__n">{daysLeft}</span>
                  <span className="muted">{t("status.days_left")}</span>
                </div>
              )}
              <div className="hypercare__actions">
                <Button
                  variant="primary"
                  disabled={(migration.data?.open_blocking_reviews ?? 0) > 0 || signoffBusy}
                  onClick={() => {
                    setSignoffError(null);
                    setLocking(true);
                  }}
                >
                  {signoffBusy ? t("signoff.working") : t("status.lock")}
                </Button>
                {signoffWatch.stalled ? (
                  <span className="muted" role="status" data-testid="signoff-stalled">
                    {t("signoff.still_processing")}
                  </span>
                ) : null}
              </div>
            </section>
          ) : null}
          {state === "locked" ? <p>{t("status.locked")}</p> : null}

          {/* xmg-actions-first: the sign-off above is what the operator came for; the data below is collapsed. */}
          <div className="disclosurelist">
            <DeltaPanel migrationId={id} />
            <AttachmentsPanel migrationId={id} />
            <EvidencePanel migrationId={id} />
            <Timeline events={events.events} />
          </div>

          <ConfirmDialog
            open={locking}
            title={t("status.lock_confirm_title")}
            onCancel={() => setLocking(false)}
            confirmDisabled={signoffBusy}
            onConfirm={() =>
              signoffBusy
                ? undefined
                : void signoff
                    .mutateAsync({})
                    .then(() => setLocking(false))
                    .catch((caught: unknown) => {
                      setLocking(false);
                      setSignoffError(describeApiError(caught, t("error.unexpected")));
                    })
            }
          >
            <p>{t("status.lock_confirm_body")}</p>
          </ConfirmDialog>
          {signoffError === null ? null : <Banner tone="block">{signoffError}</Banner>}
        </>
      )}
    </MigrationPage>
  );
}
